import { useState, useEffect, useCallback } from 'react';

const GAME_ID              = process.env.REACT_APP_UNITY_GAME_ID || '6082473';
const REWARDED_PLACEMENT   = 'Rewarded_Android';
const INTERSTITIAL_PLACEMENT = 'Interstitial_Android';

/**
 * Wraps the Unity Ads Web SDK.
 * Returns { ready, showAd }
 *  - ready:   true once SDK is initialized and ad is loaded
 *  - showAd:  call with { onReward, onSkip, onFail } callbacks
 */
export default function useUnityAds() {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // SDK is injected via script tag in index.html; wait for it to be ready
    const check = setInterval(() => {
      if (window.unityads) {
        clearInterval(check);
        try {
          window.unityads.initialize(GAME_ID, {
            onComplete:  () => {},
            onFailed:    () => {},
          });
          // Give it a moment to load the placement
          setTimeout(() => setReady(true), 1500);
        } catch (e) {
          console.warn('[UnityAds] init error:', e);
          setReady(false);
        }
      }
    }, 300);

    return () => clearInterval(check);
  }, []);

  const showAd = useCallback(({ onReward, onSkip, onFail } = {}) => {
    if (!window.unityads) { onFail?.('SDK not loaded'); return; }
    try {
      window.unityads.show(REWARDED_PLACEMENT, {
        onComplete:  () => { onReward?.(); },
        onSkipped:   () => { onSkip?.(); },
        onFailed:    (_pid, _err, msg) => { onFail?.(msg); },
        onStart:     () => {},
      });
    } catch (e) {
      onFail?.(e.message);
    }
  }, []);

  const showInterstitial = useCallback(({ onComplete, onSkip, onFail } = {}) => {
    if (!window.unityads) { onFail?.('SDK not loaded'); return; }
    try {
      window.unityads.show(INTERSTITIAL_PLACEMENT, {
        onComplete:  () => { onComplete?.(); },
        onSkipped:   () => { onSkip?.(); },
        onFailed:    (_pid, _err, msg) => { onFail?.(msg); },
        onStart:     () => {},
      });
    } catch (e) {
      onFail?.(e.message);
    }
  }, []);

  return { ready, showAd, showInterstitial };
}
